import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PublicNav from '../components/public/PublicNav';
import PublicFooter from '../components/public/PublicFooter';
import '../styles/public.css';
import { submitPrayerRequest, useWebsiteImages, slotBackground, quote } from '../lib/publicContent';
import { ed, usePageMeta } from '../lib/editTexts';


const inputStyle = (disabled) => ({
  width: '100%', padding: '13px 15px',
  border: '1px solid rgba(120,100,70,0.3)', borderRadius: 4,
  fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 16,
  background: disabled ? '#F1ECE3' : '#fff', color: '#26211C',
  outline: 'none', opacity: disabled ? 0.55 : 1,
  boxSizing: 'border-box',
});

export default function PrayerRequest() {
  const { t, i18n } = useTranslation();
  const slots = useWebsiteImages();
  usePageMeta('prayer');
  const [form, setForm] = useState({ name: '', anonymous: false, request: '', contact: false, phone: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.request.trim()) { setError('prayer.error.empty'); return; }
    setSending(true);
    try {
      await submitPrayerRequest({
        name: form.anonymous ? '' : form.name,
        request: form.request,
        contactRequested: !form.anonymous && form.contact,
        phoneNumber: form.phone,
      });
      setSent(true);
    } catch {
      setError('prayer.error.failed');
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ name: '', anonymous: false, request: '', contact: false, phone: '' });
    setSent(false); setError('');
  };

  return (
    <div className="kac-public">
      <div className="kac-inner">
        <PublicNav />

        {/* HERO */}
        <div className="kac-hero">
          <h1 {...ed('prayer.title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 78, lineHeight: 0.95, margin: '0 0 24px', maxWidth: 800 }}>
            {t('prayer.title')}
          </h1>
          <p {...ed('prayer.description')} style={{ fontSize: 19, lineHeight: 1.65, color: '#5e554b', maxWidth: 600, margin: 0 }}>
            {t('prayer.description')}
          </p>
        </div>

        {/* BODY */}
        <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.1fr', gap: 56, padding: '0 4px', marginBottom: 40 }}>
          {/* Left */}
          <div>
            <blockquote {...ed('prayer.verse')} style={{ borderLeft: '3px solid #C0291F', paddingLeft: 24, margin: '0 0 12px', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 500, fontSize: 24, lineHeight: 1.45, color: '#26211C' }}>
              {quote(i18n.language, t('prayer.verse'))}
            </blockquote>
            <p {...ed('prayer.verse_reference')} style={{ fontSize: 15, color: '#857A6E', marginBottom: 32, paddingLeft: 24 }}>{t('prayer.verse_reference')}</p>
            <div data-slot="page.prayer" style={{ height: 360, ...slotBackground(slots, 'page.prayer', 0, '/kac-assets/photos/DSC00933.jpg'), backgroundSize: 'cover', borderRadius: 6 }} />
          </div>

          {/* Right – form */}
          <div className="kac-card" style={{ padding: '44px' }}>
            {!sent ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Name */}
                <div>
                  <label htmlFor="prayer-name" {...ed('prayer.name_label')} style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#857A6E', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('prayer.name_label')}
                  </label>
                  <input
                    id="prayer-name"
                    type="text"
                    value={form.name}
                    disabled={form.anonymous}
                    onChange={e => set('name', e.target.value)}
                    placeholder={t('prayer.name_placeholder')}
                    style={inputStyle(form.anonymous)}
                    {...ed('prayer.name_placeholder')}
                  />
                </div>

                {/* Anonymous */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 15, color: '#5e554b' }}>
                  <input type="checkbox" checked={form.anonymous} onChange={e => set('anonymous', e.target.checked)} style={{ width: 16, height: 16, accentColor: '#C0291F' }} />
                  <span {...ed('prayer.anonymous')}>{t('prayer.anonymous')}</span>
                </label>

                {/* Request */}
                <div>
                  <label htmlFor="prayer-text" {...ed('prayer.request_label')} style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#857A6E', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('prayer.request_label')}
                  </label>
                  <textarea
                    id="prayer-text"
                    rows={6}
                    value={form.request}
                    onChange={e => { set('request', e.target.value); setError(''); }}
                    placeholder={t('prayer.request_placeholder')}
                    style={{ ...inputStyle(false), resize: 'vertical' }}
                    {...ed('prayer.request_placeholder')}
                  />
                </div>

                {/* Contact */}
                {!form.anonymous && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 15, color: '#5e554b' }}>
                    <input type="checkbox" checked={form.contact} onChange={e => set('contact', e.target.checked)} style={{ width: 16, height: 16, accentColor: '#C0291F' }} />
                    <span {...ed('prayer.contact')}>{t('prayer.contact')}</span>
                  </label>
                )}
                {!form.anonymous && form.contact && (
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => set('phone', e.target.value)}
                    placeholder={t('prayer.phone_placeholder')}
                    style={inputStyle(false)}
                    {...ed('prayer.phone_placeholder')}
                  />
                )}

                {/* Error */}
                {error && (
                  <div {...ed(error)} style={{ background: '#FBEAE8', border: '1px solid rgba(192,41,31,0.25)', borderRadius: 4, padding: '12px 16px', fontSize: 14, color: '#C0291F' }}>
                    {t(error)}
                  </div>
                )}

                {/* Submit */}
                <button type="button" onClick={submit} disabled={sending} className="kac-btn-red" style={{ width: '100%', textAlign: 'center', opacity: sending ? 0.65 : 1 }} {...ed(sending ? 'prayer.sending' : 'prayer.submit')}>
                  {sending ? t('prayer.sending') : t('prayer.submit')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 20, padding: '20px 0' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#1F8A5B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M5 14l7 7L23 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <h3 {...ed('prayer.thanks')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 26, margin: 0 }}>{t('prayer.thanks')}</h3>
                <p {...ed('prayer.success')} style={{ fontSize: 16, color: '#5e554b', maxWidth: 320, margin: 0, lineHeight: 1.6 }}>
                  {t('prayer.success')}
                </p>
                <button type="button" onClick={reset} {...ed('prayer.again')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#C0291F', fontFamily: "'Hanken Grotesk',sans-serif" }}>
                  {t('prayer.again')}
                </button>
              </div>
            )}
          </div>
        </div>

        <PublicFooter />
      </div>
    </div>
  );
}
